import { CKButton } from '@clearkit/react';
import startCase from 'lodash/startCase';
import { Mutation, Query } from 'react-apollo';
import { Link } from 'react-router-dom';

import ConfirmDeleteModal from '~/components/ConfirmDeleteModal';
import Footer from '~/components/Footer';
import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import ToastContainer from '~/containers/Toasts';
import Check from '~/images/destinations/Check';
import {
  destinationNameForType,
  destinationSubtitleForType,
  destinationTypeCustomHtml,
  destinationTypeEmail,
  destinationTypeSalesforce,
  destinationTypeSlack,
  destinationTypeWebhook,
} from '~/lib/destinations';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { trackDestinationDeleted } from '~/lib/segmentAnalytics';
import { legacySegmentType } from '~/lib/segments';
import Logo from '~/pages/destinations/components/Logo';
import Notice from '~/pages/destinations/components/Notice';
import Arrow from '~/pages/destinations/New/arrow.svg';
import CustomHtml from '~/pages/destinations/New/CustomHtml';
import Email from '~/pages/destinations/New/Email';
import Salesforce from '~/pages/destinations/New/Salesforce';
import Slack from '~/pages/destinations/New/Slack';
import tile from '~/pages/destinations/New/tile.png';
import Webhook from '~/pages/destinations/New/Webhook';

import destroyMutation from './destroy.graphql';
import query from './query.graphql';
import styles from './styles';
import updateMutation from './update.graphql';

const pluralizeRecipients = (recipients) => {
  if (recipients.length > 1) {
    return `${recipients.length} recipients`;
  }
  return recipients[0];
};

const destinationStatus = (destination) => {
  switch (destination.type) {
    case destinationTypeCustomHtml:
      return 'Will append your Custom HTML';
    case destinationTypeEmail:
      return `Will deliver ${
        destination.config.frequency
      } to ${pluralizeRecipients(destination.config.recipients)}`;
    case destinationTypeSalesforce:
      return `Will post a ${startCase(destination.config.object)} message`;
    case destinationTypeSlack:
      return `Bot message/channel configured`;
    default:
      return `Webhook configured`;
  }
};

class SegmentDestinationEditPage extends React.Component {
  state = {
    errors: [],
    input: {},
    showDeleteModal: false,
  };

  handleInputChange = (input) => {
    this.setState({ input });
  };

  handleDestroyComplete = (destination, segment) => {
    const path = paths.segmentDestinations(segment.type, segment.id);

    trackDestinationDeleted(destination, segment);

    ToastContainer.addSuccess('Destination removed');

    history.push(path);
  };

  handleUpdate = (updateDestination, destination) => {
    const { input } = this.state;

    updateDestination({
      variables: {
        input: {
          id: destination.id,
          ...input,
        },
      },
    });
  };

  handleUpdateComplete = ({ response: { node, errors } }) => {
    if (errors.length) {
      ToastContainer.addError('There was a problem saving this destination.');
      this.setState({ errors });
      return;
    }

    ToastContainer.addSuccess('Destination saved');

    const { segment } = node;

    const path = paths.segmentDestinations(segment.type, segment.id);
    history.push(path);
  };

  render() {
    const { classes, destinationId, segmentId, segmentType } = this.props;
    const { errors } = this.state;

    const attributeType = legacySegmentType(segmentType);

    if (!destinationId)
      return (
        <div className={classes.root}>
          <LoadingArea loading />
        </div>
      );

    const variables = {
      attributeType,
      destinationId,
      segmentId,
      segmentType,
    };

    return (
      <form className={classes.root}>
        <Query query={query} variables={variables}>
          {({ data = {}, error, loading, refetch }) => {
            if (error) {
              return (
                <GraphqlError
                  error={error}
                  refetch={refetch}
                  loading={loading}
                />
              );
            }

            if (loading) {
              return <LoadingArea loading />;
            }

            const { segment, attributes } = data;

            if (!segment) {
              return null;
            }

            const { destination, collection } = segment;

            if (!destination) {
              return null;
            }

            const { accountConnector } = destination;

            const destinationName = destinationNameForType(destination.type);
            const destinationSubtitle = destinationSubtitleForType(
              destination.type,
              segment.type,
            );

            return (
              <>
                <div className={classes.header}>
                  <h1 className={classes.heading}>
                    Set up {destinationName} Destination
                  </h1>

                  <span className={classes.subtitle}>
                    {destinationSubtitle}
                  </span>

                  <div className={classes.flow}>
                    <div className={classes.flowBox}>
                      <img alt="" className={classes.tile} src={tile} />
                      <div className={classes.flowDetail}>
                        <span>{segment.name}</span>
                        <span className={classes.detailSub}>
                          {collection?.name}
                        </span>
                      </div>
                    </div>

                    <Arrow className={classes.flowArrow} />

                    <div className={classes.flowBox}>
                      <div className={classes.logo}>
                        <Logo
                          destinationType={destination.type}
                          width="40"
                          height="40"
                        />
                      </div>

                      <div className={classes.flowDetail}>
                        <span>{destinationName}</span>
                        <span className={classes.detailSub}>
                          <Check className={classes.check} />
                          {destinationStatus(destination)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Notice type={destination.type} typename={segment.type} />
                <div className={classes.content}>
                  {(() => {
                    switch (destination.type) {
                      case destinationTypeCustomHtml:
                        return (
                          <CustomHtml
                            destination={destination}
                            errors={errors}
                            onInputChange={this.handleInputChange}
                            typename={segment.type}
                          />
                        );

                      case destinationTypeEmail:
                        return (
                          <Email
                            attributes={attributes}
                            destination={destination}
                            errors={errors}
                            onInputChange={this.handleInputChange}
                            segmentId={segmentId}
                            segmentType={segmentType}
                            typename={segment.type}
                          />
                        );

                      case destinationTypeSalesforce:
                        return (
                          <Salesforce
                            accountConnector={accountConnector}
                            destination={destination}
                            errors={errors}
                            onInputChange={this.handleInputChange}
                            typename={segment.type}
                          />
                        );

                      case destinationTypeSlack:
                        return (
                          <Slack
                            accountConnector={data.slackAccountConnector}
                            attributes={attributes}
                            destination={destination}
                            errors={errors}
                            onInputChange={this.handleInputChange}
                            refetch={refetch}
                            segmentId={segmentId}
                            segmentType={segmentType}
                            typename={segment.type}
                          />
                        );

                      case destinationTypeWebhook:
                        return (
                          <Webhook
                            attributes={attributes}
                            destination={destination}
                            errors={errors}
                            onInputChange={this.handleInputChange}
                            segmentId={segmentId}
                            segmentType={segmentType}
                            typename={segment.type}
                          />
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>

                <Mutation
                  mutation={destroyMutation}
                  onCompleted={() => {
                    this.handleDestroyComplete(destination, segment);
                  }}
                  variables={{
                    input: {
                      id: destination.id,
                    },
                  }}
                  refetchQueries={['SegmentDestinationsPage']}>
                  {(mutation) => (
                    <ConfirmDeleteModal
                      open={this.state.showDeleteModal}
                      canDelete
                      type="destination"
                      item={destination}
                      deleteItem={mutation}
                      onCompleted={() => this.setState({ menuOpen: false })}
                      closeModal={() => {
                        this.setState({
                          showDeleteModal: false,
                        });
                      }}
                    />
                  )}
                </Mutation>

                <Footer
                  left={
                    <CKButton
                      disabled={loading}
                      onClick={() => {
                        this.setState({ showDeleteModal: true });
                      }}
                      variant="secondaryRed">
                      Remove
                    </CKButton>
                  }
                  right={
                    <>
                      <CKButton
                        as={Link}
                        to={paths.segmentDestinations(segment.type, segment.id)}
                        variant="secondary">
                        Cancel
                      </CKButton>
                      <Mutation
                        mutation={updateMutation}
                        onCompleted={this.handleUpdateComplete}
                        refetchQueries={['SegmentDestinationsPage']}>
                        {(updateDestination, { loading }) => (
                          <CKButton
                            disabled={loading}
                            onClick={() => {
                              this.handleUpdate(updateDestination, destination);
                            }}
                            type="button"
                            variantColor="green"
                            variant="bold">
                            Save changes
                          </CKButton>
                        )}
                      </Mutation>
                    </>
                  }
                />
              </>
            );
          }}
        </Query>
      </form>
    );
  }
}

SegmentDestinationEditPage.propTypes = {
  classes: PropTypes.object.isRequired,
  destinationId: PropTypes.string,
};

export default withStyles(styles)(SegmentDestinationEditPage);
